import React from 'react'
import {
  ButtonVariant,
  ImageCard,
  MaxWidthConstraint,
  OvalButton,
  PhotoHero,
  Spacer,
  WorkArea,
} from '@revelrygroup/components'
import classnames from 'classnames'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import Image from 'next/image'
import parsePhoneNumber from 'libphonenumber-js'

import { API } from '../revelry_api'
import EventTrackingModel from '../components/models/EventTrackingModel'
import { FuneralHome } from '../types'
import { FuneralHomeFooter } from '../components/FuneralHomeFooter'
import { FuneralHomeModel } from '../components/models/FuneralHomeModel'
import H3 from '../components/typography/H3'
import Layout from '../components/Layout'
import styles from '../styles/Contact.module.scss'

interface Props {
  funeralHome: FuneralHome | null
}

const location1Address = (funeralHome: FuneralHome): string => {
  if (funeralHome.address_line_2) {
    return `${funeralHome.address_line_1} (${funeralHome.address_line_2}), ${funeralHome.city}, ${funeralHome.state} ${funeralHome.zip}`
  }

  return `${funeralHome.address_line_1}, ${funeralHome.city}, ${funeralHome.state} ${funeralHome.zip}`
}

const location2Address = (funeralHome: FuneralHome): string => {
  if (funeralHome.location_2_address_line_2) {
    return `${funeralHome.location_2_address_line_1} (${funeralHome.location_2_address_line_2}), ${funeralHome.location_2_city}, ${funeralHome.location_2_state} ${funeralHome.location_2_zip}`
  }

  return `${funeralHome.location_2_address_line_1}, ${funeralHome.location_2_city}, ${funeralHome.location_2_state} ${funeralHome.location_2_zip}`
}

const Contact: React.FC<Props> = ({ funeralHome }) => {
  if (!funeralHome) {
    return null
  }

  const address1 = location1Address(funeralHome)
  const address2 = location2Address(funeralHome)

  return (
    <>
      <Head>
        <title>
          Contact Information - {funeralHome.name} - {FuneralHomeModel.headTagLocation(funeralHome)}
        </title>

        <meta
          name="description"
          key="description"
          content={`Get contat information for ${
            funeralHome.name
          } - ${FuneralHomeModel.headTagLocation(
            funeralHome,
          )} - address, email, phone number. Contact us for consultation.`}
        />
      </Head>

      <Layout>
        <PhotoHero>Contact Us</PhotoHero>

        <div className={styles.container}>
          <main>
            <WorkArea style={{ backgroundColor: 'white' }}>
              <MaxWidthConstraint maxWidth={976}>
                <H3 className="centered-text">
                  We are always here for you. Whether you need immediate help, or would simply like
                  to talk to one of our Directors, we&apos;re only a phone call away.
                </H3>
              </MaxWidthConstraint>
            </WorkArea>

            <WorkArea className="blue-gradient-background">
              <MaxWidthConstraint>
                <ImageCard
                  className={styles.contactCard}
                  imageAlt={funeralHome.name}
                  imageURL={funeralHome.logo_image}
                >
                  <Image alt="" height={46} src="/kalas-radian--blue.png" width={46} />

                  <Spacer verticalTop="xs" verticalBottom="sm">
                    <p className="body">{funeralHome.name}</p>
                    <H3>{funeralHome.city}</H3>
                  </Spacer>

                  <Spacer verticalBottom="sm">
                    <p className="body">{address1}</p>
                    <a
                      className={classnames('link', styles.directions)}
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        address1,
                      )}`}
                      onClick={() => EventTrackingModel.trackGetDirections()}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Get Directions
                    </a>
                  </Spacer>

                  <p className="body">
                    <strong>{parsePhoneNumber(funeralHome.phone_number).formatNational()}</strong>
                  </p>

                  <Spacer verticalTop="sm">
                    <Spacer horizontalRight="xs" inline>
                      <OvalButton
                        href={`tel:${funeralHome.phone_number}`}
                        onClick={() => EventTrackingModel.trackContactPhone()}
                        variant={ButtonVariant.DEFAULT_DARK}
                        as="anchor"
                      >
                        Call Us Anytime
                      </OvalButton>
                    </Spacer>

                    <OvalButton
                      href={`mailto:${funeralHome.email_address}`}
                      onClick={() => EventTrackingModel.trackContactEmail()}
                      variant={ButtonVariant.INVERSE_LIGHT}
                      as="anchor"
                    >
                      Email Us
                    </OvalButton>
                  </Spacer>
                </ImageCard>
              </MaxWidthConstraint>

              {FuneralHomeModel.hasSecondLocation(funeralHome) && (
                <MaxWidthConstraint>
                  <ImageCard
                    className={styles.contactCard}
                    imageAlt={funeralHome.name}
                    imageURL={funeralHome.location_2_logo_image}
                  >
                    <Image height={46} src="/kalas-radian--blue.png" width={46} />

                    <Spacer verticalTop="xs" verticalBottom="sm">
                      <p className="body">{funeralHome.name}</p>
                      <H3>National Harbor</H3>
                    </Spacer>

                    <Spacer verticalBottom="sm">
                      <p className="body">{address2}</p>
                      <a
                        className={classnames('link', styles.directions)}
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                          address2,
                        )}`}
                        onClick={() => EventTrackingModel.trackGetDirections()}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Get Directions
                      </a>
                    </Spacer>

                    <p className="body">
                      <strong>
                        {parsePhoneNumber(funeralHome.location_2_phone_number).formatNational()}
                      </strong>
                    </p>

                    <Spacer verticalTop="sm">
                      <Spacer horizontalRight="xs" inline>
                        <OvalButton
                          href={`tel:${funeralHome.location_2_phone_number}`}
                          onClick={() => EventTrackingModel.trackContactPhone()}
                          variant={ButtonVariant.DEFAULT_DARK}
                          as="anchor"
                        >
                          Call Us Anytime
                        </OvalButton>
                      </Spacer>

                      <OvalButton
                        href={`mailto:${funeralHome.location_2_email_address}`}
                        onClick={() => EventTrackingModel.trackContactEmail()}
                        variant={ButtonVariant.INVERSE_LIGHT}
                        as="anchor"
                      >
                        Email Us
                      </OvalButton>
                    </Spacer>
                  </ImageCard>
                </MaxWidthConstraint>
              )}
            </WorkArea>
          </main>

          <FuneralHomeFooter />
        </div>
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async () => {
  try {
    const funeralHome = await API.funeralHomes.load()

    return { props: { funeralHome } }
  } catch (e) {
    return { props: { funeralHome: null } }
  }
}

export default Contact
